import { io } from 'socket.io-client';
import { API_BASE_URL } from './apiClient';
import { useAuthStore } from '../store/authStore';

/**
 * Backend origin for the Socket.IO connection. An absolute API base
 * (http://localhost:3000/api/v1 in dev) yields that origin; a relative one
 * (/api/v1 behind a same-origin proxy in prod) falls back to the SPA origin.
 */
function backendOrigin() {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

let socket = null;

/** Lazy app-wide socket. Auth callback re-reads the store on every (re)connect
 *  attempt, so reconnects after a token refresh carry the fresh JWT. */
export function getSocket() {
  if (!socket) {
    socket = io(`${backendOrigin()}/realtime`, {
      auth: (cb) => cb({ token: useAuthStore.getState().accessToken }),
      reconnectionDelayMax: 10_000,
    });
  }
  return socket;
}

/**
 * Join a chat thread's live feed.
 *  - onMessage(message): a hydrated message pushed by the server.
 *  - onLive(boolean): true once the join is acknowledged, false on disconnect —
 *    callers use it to toggle their polling fallback.
 * Returns an unsubscribe() that leaves the room and detaches the listeners.
 * When realtime is disabled server-side the socket just retries quietly and
 * the ack never fires, so consumers stay in polling mode.
 */
export function subscribeToThread(threadId, { onMessage, onLive } = {}) {
  const s = getSocket();
  let active = true;
  const join = () =>
    s.emit('thread:join', { threadId }, (ack) => active && onLive?.(Boolean(ack?.ok)));
  const handleMessage = (msg) => {
    if (msg?.threadId === threadId) onMessage?.(msg);
  };
  const handleConnect = () => join(); // re-join rooms after reconnects
  const handleDisconnect = () => onLive?.(false);

  s.on('chat.message.created', handleMessage);
  s.on('connect', handleConnect);
  s.on('disconnect', handleDisconnect);
  if (s.connected) join();

  return () => {
    active = false;
    if (s.connected) s.emit('thread:leave', { threadId });
    s.off('chat.message.created', handleMessage);
    s.off('connect', handleConnect);
    s.off('disconnect', handleDisconnect);
  };
}

/** Tear down the socket (logout). */
export function disconnectSocket() {
  socket?.disconnect();
  socket = null;
}
